import { LoadingButton } from "@mui/lab";
import { useTranslation } from "react-i18next";
import { Box, IconButton } from "@mui/material";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useParams } from "react-router-dom";

import APP_ROUTES from "../../../../routers/routeConstants";

import useIsOwn from "../../utils/useIsOwn";
import { CreatePostFormData } from "../../api/types/feed.types";
import { createPostSchema } from "../../schemas/createPost.schema";
import { TagsField } from "../../components/basicPostFields/TagsField";
import { CreatePostArrowBackIcon } from "../../assets/icons/create-feed";
import { MediaField } from "../../components/basicPostFields/MediaField";
import { usePostDetailsQuery } from "../../api/queries/usePostDetailsQuery";
import { PrivacyField } from "../../components/basicPostFields/PrivacyField";
import { DescriptionField } from "../../components/basicPostFields/DescriptionField";
import { AchievementsField } from "../../components/basicPostFields/AchievementsField";
import { useUpdateBasicPostMutation } from "../../api/mutations/useUpdateBasicPostMutation";

const EditPostPage = () => {
  const navigate = useNavigate();
  const { postId } = useParams();
  const { data, isLoading } = usePostDetailsQuery(Number(postId));
  const { updatePost, isUpdating, error } = useUpdateBasicPostMutation({ postId: Number(postId) });
  const { t } = useTranslation("feed", { keyPrefix: "postForm" });

  const { isOwn, isLoading: isOwnLoading } = useIsOwn({
    authorId: data?.post.author.id,
    isAuthorIdLoading: isLoading,
  });

  if ((!isOwn && !isOwnLoading) || error) {
    navigate(APP_ROUTES.NOT_FOUND);
  }

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CreatePostFormData>({
    resolver: zodResolver(createPostSchema),
    values: data
      ? {
          description: data.post.description || "",
          media: data.post.media || [],
          tags: {
            old: data.post.tags?.filter((tag) => tag.id).map((tag) => tag.id.toString()) || [],
            new: data.post.tags?.filter((tag) => !tag.id).map((tag) => tag.title) || [],
          },
          privacy: data.post.privacy || "PUBLIC",
          achievementId: data.post.achievementId || null,
          selectedUsers: data.post?.selectedUsers || [],
          selectedGroups: data.post?.selectedGroups || [],
        }
      : undefined,
    defaultValues: {
      description: "",
      media: [],
      tags: {
        old: [],
        new: [],
      },
      privacy: "PUBLIC",
      achievementId: null,
      selectedUsers: [],
      selectedGroups: [],
    },
    mode: "onChange",
  });

  const allValues = useWatch({ control });

  if (isLoading || !data) {
    return null;
  }

  const handleFormSubmit = handleSubmit((formData: CreatePostFormData) => {
    const dataToSubmit = {
      ...formData,
      selectedUsers: formData.privacy === "PRIVATE" ? formData.selectedUsers : [],
      selectedGroups: formData.privacy === "PRIVATE" ? formData.selectedGroups : [],
    };
    updatePost(dataToSubmit);
  });

  return (
    <Box
      sx={{
        borderRadius: "8px",
        border: "1px solid #E7E7E7",
        background: "rgba(255, 255, 255, 0.90)",
        width: "100%",
        padding: "24px 16px",
        display: "flex",
        flexDirection: "column",
        gap: "40px",
      }}
    >
      <IconButton sx={{ width: "fit-content" }} onClick={() => navigate("/feed")}>
        <CreatePostArrowBackIcon />
      </IconButton>

      <Box component="form" onSubmit={handleFormSubmit} sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
        <Box sx={{ display: "flex", gap: "48px", flexDirection: { xs: "column", sm: "row" } }}>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "24px" }}>
            <DescriptionField control={control} name="description" error={errors.description?.message} />
            <AchievementsField control={control} name="achievementId" error={errors.achievementId?.message} />
            <TagsField control={control} name="tags" error={errors.tags?.message} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <MediaField control={control} name="media" error={errors.media?.message} />
          </Box>
        </Box>

        <PrivacyField control={control} name="privacy" error={errors.privacy?.message} />

        <LoadingButton
          variant="primary-md"
          type="submit"
          disabled={isSubmitting}
          sx={{ width: "fit-content", marginLeft: "auto" }}
          loading={isUpdating}
        >
          {t("publish")}
        </LoadingButton>
      </Box>
    </Box>
  );
};

export default EditPostPage;
